import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Github, Mail } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hungry-elion-4/artifacts/ac0h19e4_KritvaSoftLogoOriginal-removebg-preview.png";

const footerLinks = {
  company: [
    { name: 'About', href: '#about' },
    { name: 'Careers', href: '/careers', isRoute: true },
    { name: 'Contact', href: '#contact' },
  ],
  services: [
    { name: 'Services', href: '#services' },
    { name: 'Solutions', href: '#solutions' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy-policy', isRoute: true },
    { name: 'Terms of Service', href: '/terms-of-service', isRoute: true },
  ],
};

const socialLinks = [
  { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/kritvasoft' },
  { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/kritvasoft' },
  { name: 'GitHub', icon: Github, href: 'https://github.com/kritvasoft' },
  { name: 'Email', icon: Mail, href: 'mailto:hello@kritvasoft.com' },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      data-testid="footer-section"
      className="w-full border-t"
      style={{ 
        backgroundColor: '#FFFFFF',
        borderColor: '#E2E8F0'
      }}
    >
      <div className="kv-container py-10 lg:py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a href="#home" className="inline-block mb-4">
              <img 
                src={LOGO_URL} 
                alt="KritvaSoft" 
                className="h-9 lg:h-10 w-auto"
                loading="lazy"
                width="120"
                height="40"
              />
            </a>
            <p 
              className="text-sm leading-relaxed max-w-sm mb-6"
              style={{ color: '#475569' }}
            >
              Empowering businesses with innovative software solutions. 
              Build. Automate. Grow.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-link-${social.name.toLowerCase()}`}
                  className="p-2 rounded-md transition-colors duration-200"
                  style={{ color: '#475569' }}
                  onMouseEnter={(e) => e.target.closest('a').style.color = '#0A3D91'}
                  onMouseLeave={(e) => e.target.closest('a').style.color = '#475569'}
                  aria-label={social.name}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 
              className="text-sm font-semibold uppercase tracking-wider mb-4"
              style={{ color: '#131B2E' }}
            >
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-sm transition-colors duration-200"
                      style={{ color: '#475569' }}
                      onMouseEnter={(e) => e.target.style.color = '#0A3D91'}
                      onMouseLeave={(e) => e.target.style.color = '#475569'}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-sm transition-colors duration-200"
                      style={{ color: '#475569' }}
                      onMouseEnter={(e) => e.target.style.color = '#0A3D91'}
                      onMouseLeave={(e) => e.target.style.color = '#475569'}
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 
              className="text-sm font-semibold uppercase tracking-wider mb-4"
              style={{ color: '#131B2E' }}
            >
              Services
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-sm transition-colors duration-200"
                    style={{ color: '#475569' }}
                    onMouseEnter={(e) => e.target.style.color = '#0A3D91'}
                    onMouseLeave={(e) => e.target.style.color = '#475569'}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 
              className="text-sm font-semibold uppercase tracking-wider mb-4"
              style={{ color: '#131B2E' }}
            >
              Legal
            </h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-sm transition-colors duration-200"
                      style={{ color: '#475569' }}
                      onMouseEnter={(e) => e.target.style.color = '#0A3D91'}
                      onMouseLeave={(e) => e.target.style.color = '#475569'}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-sm transition-colors duration-200"
                      style={{ color: '#475569' }}
                      onMouseEnter={(e) => e.target.style.color = '#0A3D91'}
                      onMouseLeave={(e) => e.target.style.color = '#475569'}
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div 
          className="mt-8 pt-6 border-t"
          style={{ borderColor: '#E2E8F0' }}
        >
          <p 
            className="text-sm text-center"
            style={{ color: '#475569' }}
          >
            &copy; {currentYear} KritvaSoft. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
